import { FC, useEffect } from "react";

import { logger } from "@moodys/mdc-frontend.services.logger";
import { EventBusTopic } from "@moodys/mdc-frontend.utils.event-bus";
import { ErrorPage as Error } from "@moodys/mdc-frontend.widgets.error-page";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSearchParams } from "react-router-dom";

import { useEventBus } from "../../hooks/useEventBus";

interface ErrorRouteParams {
  isServerError?: boolean;
  logError?: boolean;
}

export const ErrorPage: FC<ErrorRouteParams> = ({
  logError = false,
  isServerError = false,
}) => {
  const [params] = useSearchParams();
  const statusCode = isServerError ? 500 : 404;
  const pageTitle = isServerError
    ? "Error | Moody's"
    : "Page not found | Moody's";
  const correlationId = params.get("id") ?? undefined;
  const previousPage = params.get("previousPage");
  const [_, setPageMeta] = useEventBus(EventBusTopic.Shell.PageMeta);

  useEffect(() => {
    setPageMeta({
      title: pageTitle,
    });
  }, [pageTitle]);

  useEffect(() => {
    if (logError) {
      logger.error({
        message: "Redirected to the error page",
        statusCode,
        ...(correlationId ? { correlationId } : {}),
        ...(previousPage ? { previousPage } : {}),
      });
    }
  }, [logError, statusCode, correlationId, previousPage]);

  const handleServerErrorReloadClick = () => {
    if (!previousPage) {
      return;
    }
    window.location.replace(previousPage);
  };

  return (
    <Error
      statusCode={statusCode}
      correlationId={correlationId}
      hideServerErrorReloadButton={!previousPage}
      serverErrorReloadButtonProps={{
        buttonText: "Go Back",
        descriptiveMessage: `We're having difficulty loading the page you requested.
      Our technical team has been alerted to the issue.
      In the meantime, you can go back or contact us at <a href='mailto:clientservices@moodys.com'>clientservices@moodys.com</a> for additional assistance.`,
        onClick: handleServerErrorReloadClick,
        startIcon: <ArrowBackIcon />,
      }}
      hideIncidentIdDisplay
    />
  );
};
